import React, { createContext, useContext, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/hooks/auth";
import { 
  createClass, 
  createSubscription,
  createGiftCard, 
  deleteClass, 
  updateBookingPaymentStatus, 
  updateSubscriptionStatus,
  updateSubscriptionPaymentStatus,
  editSubscription,
  deleteSubscription,
  rescheduleBooking,
  scheduleClasses,
  deleteBooking
} from "@/lib/supabase";
import { UserType, ClassType, GiftCardType } from "@/lib/supabase/types";
import { format } from "date-fns";

type AdminDashboardContextType = {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  newClass: Omit<ClassType, "id">;
  setNewClass: React.Dispatch<React.SetStateAction<Omit<ClassType, "id">>>;
  newSubscription: { userId: string; totalClasses: number };
  setNewSubscription: React.Dispatch<React.SetStateAction<{ userId: string; totalClasses: number }>>;
  isCreatingGiftCard: boolean;
  selectedUser: UserType | null;
  setSelectedUser: React.Dispatch<React.SetStateAction<UserType | null>>;
  isAddClassOpen: boolean;
  setIsAddClassOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isScheduleDialogOpen: boolean;
  setIsScheduleDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isScheduling: boolean;
  scheduleResult: { success: boolean; created: number; error?: string } | null;
  isAddSubscriptionOpen: boolean;
  setIsAddSubscriptionOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isAddGiftCardOpen: boolean;
  setIsAddGiftCardOpen: React.Dispatch<React.SetStateAction<boolean>>;
  
  handleSelectUser: (user: UserType) => void;
  handleCreateClass: () => Promise<void>;
  handleCreateSubscription: () => Promise<void>;
  handleCreateGiftCard: (firstName: string, lastName: string, classes: number) => Promise<void>;
  handleDeleteClass: (id: string) => Promise<void>;
  handleScheduleClasses: () => Promise<void>;
  handleUpdateSubscriptionStatus: (subscriptionId: string, active: boolean) => Promise<void>;
  handleUpdateSubscriptionPaymentStatus: (subscriptionId: string, paymentStatus: 'pending' | 'paid' | 'nptr') => Promise<void>;
  handleEditSubscription: (subscriptionId: string, updates: { totalClasses: number; remainingClasses: number; expirationDate: string }) => Promise<void>;
  handleDeleteSubscription: (subscriptionId: string) => Promise<void>;
  handleDeleteBooking: (bookingId: string, classId: string) => Promise<void>;
  handleUpdatePaymentStatus: (bookingId: string, paymentStatus: 'pending' | 'paid' | 'nptr') => Promise<void>;
  handleRescheduleBooking: (bookingId: string, oldClassId: string, newClassId: string) => Promise<void>;
};

const AdminDashboardContext = createContext<AdminDashboardContextType | undefined>(undefined);

export const AdminDashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState("bookings");
  
  const [newClass, setNewClass] = useState<Omit<ClassType, "id">>({
    title: "Pottery Class",
    day: "Saturday",
    date: format(new Date(), 'yyyy-MM-dd'),
    time: "2:00 PM",
    duration: "1.5",
    capacity: 12,
    availableSpots: 12,
    image: "",
    price: 3500
  });
  
  const [newSubscription, setNewSubscription] = useState({
    userId: '',
    totalClasses: 8
  });

  const [isCreatingGiftCard, setIsCreatingGiftCard] = useState(false);
  
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  
  const [isAddClassOpen, setIsAddClassOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleResult, setScheduleResult] = useState<{ success: boolean; created: number; error?: string } | null>(null);
  const [isAddSubscriptionOpen, setIsAddSubscriptionOpen] = useState(false);
  const [isAddGiftCardOpen, setIsAddGiftCardOpen] = useState(false);
  
  const handleSelectUser = (user: UserType) => {
    setSelectedUser(user);
    setNewSubscription({ ...newSubscription, userId: user.id });
  };
  
  const handleCreateClass = async () => {
    const result = await createClass(newClass);
    if (result.success) {
      toast.success("Class created successfully");
      setIsAddClassOpen(false);
      setNewClass({
        title: "Pottery Class",
        day: "Saturday",
        date: format(new Date(), 'yyyy-MM-dd'),
        time: "2:00 PM",
        duration: "1.5",
        capacity: 12,
        availableSpots: 12,
        image: "",
        price: 3500
      });
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] });
    } else {
      toast.error(result.error || "Failed to create class");
    }
  };
  
  const handleCreateSubscription = async () => {
    if (!newSubscription.userId) {
      toast.error("Please select a user for this subscription");
      return;
    }
    
    const result = await createSubscription(newSubscription.userId, newSubscription.totalClasses);
    if (result.success) {
      toast.success("Subscription created successfully");
      setIsAddSubscriptionOpen(false);
      setNewSubscription({
        userId: '',
        totalClasses: 8
      });
      setSelectedUser(null);
      queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
    } else {
      toast.error(result.error || "Failed to create subscription");
    }
  };

  const handleCreateGiftCard = async (firstName: string, lastName: string, classes: number) => {
    setIsCreatingGiftCard(true);
    try {
      const result = await createGiftCard(firstName, lastName, classes);
      if (result.success) {
        toast.success(`Gift card created successfully with code: ${result.data?.code}`);
        setIsAddGiftCardOpen(false);
        queryClient.invalidateQueries({ queryKey: ['admin-gift-cards'] });
      } else {
        toast.error(result.error || "Failed to create gift card");
      }
    } catch (error) {
      toast.error("An error occurred creating the gift card");
      console.error(error);
    } finally {
      setIsCreatingGiftCard(false);
    }
  };
  
  const handleDeleteClass = async (id: string) => {
    const result = await deleteClass(id);
    if (result.success) {
      toast.success("Class deleted successfully");
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] });
    } else {
      toast.error(result.error || "Failed to delete class");
    }
  };
  
  const handleScheduleClasses = async () => {
    setIsScheduling(true);
    try {
      const result = await scheduleClasses(8);
      setScheduleResult(result);
      
      if (result.success) {
        toast.success(`Successfully scheduled ${result.created} new classes`);
        queryClient.invalidateQueries({ queryKey: ['admin-classes'] });
      } else {
        toast.error(result.error || "Failed to schedule classes");
      }
    } catch (error: any) {
      setScheduleResult({
        success: false,
        created: 0,
        error: error.message || "An unexpected error occurred"
      });
      toast.error("Failed to schedule classes");
    } finally {
      setIsScheduling(false);
    }
  };
  
  const handleUpdateSubscriptionStatus = async (subscriptionId: string, active: boolean) => {
    const result = await updateSubscriptionStatus(subscriptionId, active);
    if (result.success) {
      toast.success(`Subscription ${active ? 'activated' : 'deactivated'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
    } else {
      toast.error(result.error || "Failed to update subscription status");
    }
  };

  const handleUpdateSubscriptionPaymentStatus = async (subscriptionId: string, paymentStatus: 'pending' | 'paid' | 'nptr') => {
    const result = await updateSubscriptionPaymentStatus(subscriptionId, paymentStatus);
    if (result.success) {
      const statusMessage = 
        paymentStatus === 'paid' ? 'paid' : 
        paymentStatus === 'nptr' ? 'not paid for technical reasons' : 
        'pending';
      
      toast.success(`Subscription marked as ${statusMessage}`);
      queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
    } else {
      toast.error(result.error || "Failed to update subscription payment status");
    }
  };
  
  const handleEditSubscription = async (subscriptionId: string, updates: { 
    totalClasses: number; 
    remainingClasses: number; 
    expirationDate: string;
  }) => {
    const result = await editSubscription(subscriptionId, updates);
    if (result.success) {
      toast.success("Subscription updated successfully");
      queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
    } else {
      toast.error(result.error || "Failed to update subscription");
    }
  };
  
  const handleDeleteSubscription = async (subscriptionId: string) => {
    const result = await deleteSubscription(subscriptionId);
    if (result.success) {
      toast.success("Subscription deleted successfully");
      queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
    } else {
      toast.error(result.error || "Failed to delete subscription");
    }
  };
  
  const handleDeleteBooking = async (bookingId: string, classId: string) => {
    const result = await deleteBooking(bookingId, classId);
    if (result.success) {
      toast.success("Booking deleted successfully");
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
    } else {
      toast.error(result.error || "Failed to delete booking");
    }
  };
  
  const handleUpdatePaymentStatus = async (bookingId: string, paymentStatus: 'pending' | 'paid' | 'nptr') => {
    const result = await updateBookingPaymentStatus(bookingId, paymentStatus);
    if (result.success) {
      const statusMessage = 
        paymentStatus === 'paid' ? 'paid' : 
        paymentStatus === 'nptr' ? 'not paid for technical reasons' : 
        'pending';
      
      toast.success(`Booking marked as ${statusMessage}`);
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
    } else {
      toast.error(result.error || "Failed to update payment status");
    }
  };
  
  const handleRescheduleBooking = async (bookingId: string, oldClassId: string, newClassId: string) => {
    const result = await rescheduleBooking(bookingId, oldClassId, newClassId, user?.id);
    if (result.success) {
      toast.success("Booking rescheduled successfully");
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] });
    } else {
      toast.error(result.error || "Failed to reschedule booking");
      throw new Error(result.error || "Failed to reschedule booking");
    }
  };
  
  const value = {
    selectedTab,
    setSelectedTab,
    newClass,
    setNewClass,
    newSubscription,
    setNewSubscription,
    isCreatingGiftCard,
    selectedUser,
    setSelectedUser,
    isAddClassOpen,
    setIsAddClassOpen,
    isScheduleDialogOpen,
    setIsScheduleDialogOpen,
    isScheduling,
    scheduleResult,
    isAddSubscriptionOpen,
    setIsAddSubscriptionOpen,
    isAddGiftCardOpen,
    setIsAddGiftCardOpen,
    handleSelectUser,
    handleCreateClass,
    handleCreateSubscription,
    handleCreateGiftCard,
    handleDeleteClass,
    handleScheduleClasses,
    handleUpdateSubscriptionStatus,
    handleUpdateSubscriptionPaymentStatus,
    handleEditSubscription,
    handleDeleteSubscription,
    handleDeleteBooking,
    handleUpdatePaymentStatus,
    handleRescheduleBooking,
  };
  
  return (
    <AdminDashboardContext.Provider value={value}>
      {children}
    </AdminDashboardContext.Provider>
  );
};

export const useAdminDashboard = (): AdminDashboardContextType => {
  const context = useContext(AdminDashboardContext);
  if (context === undefined) {
    throw new Error('useAdminDashboard must be used within an AdminDashboardProvider');
  }
  return context;
};
